import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { registerGlobalErrorHandler } from '../utils/globalErrorHandler.ts';

interface ErrorContextType {
  globalErrorMessage: string | null;
  isGlobalErrorModalOpen: boolean;
  displayError: (message: string, status?: number) => void;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [globalErrorMessage, setGlobalErrorMessage] = useState<string | null>(null);
  const [isGlobalErrorModalOpen, setIsGlobalErrorModalOpen] = useState<boolean>(false);

  const displayError = useCallback((message: string, status?: number) => {
    let fullMessage = message;
    if (status && status !== 0) { // status 0 typically means network error, which is already in message
      fullMessage = `Error ${status}: ${message}`;
    }
    setGlobalErrorMessage(fullMessage);
    setIsGlobalErrorModalOpen(true);
  }, []);

  const clearError = useCallback(() => {
    setGlobalErrorMessage(null);
    setIsGlobalErrorModalOpen(false);
  }, []);

  // Register this context's displayError function as the global handler
  useEffect(() => {
    registerGlobalErrorHandler(displayError);
  }, [displayError]);

  return (
    <ErrorContext.Provider value={{ globalErrorMessage, isGlobalErrorModalOpen, displayError, clearError }}>
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};